import { useEffect, useState, useMemo } from 'react'
import EmpresaLayout from '@/components/EmpresaLayout'
import { useAuth } from '@/hooks/useAuth'
import { useTalentBank, type TalentBankEntry } from '@/hooks/useTalentBank'
import {Star, Search, Tag, Trash2, Edit2, X, Users, UserCheck, SlidersHorizontal} from 'lucide-react'

const CLASSIFICATIONS = [
  'Por clasificar',
  'Potencial',
  'Estratégico',
  'Futuro',
  'Alta prioridad',
  'En seguimiento',
]

const CLASS_STYLES: Record<string, string> = {
  'Por clasificar': 'bg-slate-100 text-slate-600',
  'Potencial': 'bg-blue-50 text-blue-700',
  'Estratégico': 'bg-violet-50 text-violet-700',
  'Futuro': 'bg-emerald-50 text-emerald-700',
  'Alta prioridad': 'bg-amber-50 text-amber-700',
  'En seguimiento': 'bg-orange-50 text-orange-700',
}

function EmpresaBancoTalento() {
  const { user } = useAuth()
  const { entries, loading, fetchByCompany, updateEntry, removeEntry } = useTalentBank()

  const [search, setSearch] = useState('')
  const [filterClass, setFilterClass] = useState('')
  const [filterTag, setFilterTag] = useState('')
  const [editingEntry, setEditingEntry] = useState<TalentBankEntry | null>(null)

  useEffect(() => {
    if (user?.userId) fetchByCompany(user.userId)
  }, [user, fetchByCompany])

  const allTags = useMemo(() => {
    const set = new Set<string>()
    entries.forEach((e) => (e.tags || []).forEach((t) => set.add(t)))
    return Array.from(set).sort()
  }, [entries])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return entries.filter((e) => {
      const matchSearch =
        !q ||
        (e.collaboratorName || '').toLowerCase().includes(q) ||
        (e.collaboratorEmail || '').toLowerCase().includes(q) ||
        (e.lastVacancyTitle || '').toLowerCase().includes(q) ||
        (e.tags || []).some((t) => t.toLowerCase().includes(q))
      const matchClass = !filterClass || e.classification === filterClass
      const matchTag = !filterTag || (e.tags || []).includes(filterTag)
      return matchSearch && matchClass && matchTag
    })
  }, [entries, search, filterClass, filterTag])

  const handleDelete = async (entry: TalentBankEntry) => {
    if (!confirm(`¿Eliminar a "${entry.collaboratorName}" del banco de talento?`)) return
    try {
      await removeEntry(entry._id)
      if (user?.userId) fetchByCompany(user.userId)
    } catch (e) {
      console.error('Error al eliminar del banco de talento:', e)
      alert('No se pudo eliminar el registro.')
    }
  }

  return (
    <EmpresaLayout
      title="Banco de talento"
      subtitle="Candidatos valiosos guardados para futuras oportunidades de contratación"
    >
      {/* KPIs */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total guardados', value: entries.length, icon: Users, color: 'text-slate-700' },
          {
            label: 'Alta prioridad',
            value: entries.filter((e) => e.classification === 'Alta prioridad').length,
            icon: Star,
            color: 'text-amber-600',
          },
          {
            label: 'Estratégicos',
            value: entries.filter((e) => e.classification === 'Estratégico').length,
            icon: UserCheck,
            color: 'text-violet-600',
          },
          {
            label: 'Etiquetas únicas',
            value: allTags.length,
            icon: Tag,
            color: 'text-blue-600',
          },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Icon size={16} className={color} />
              <p className="text-xs text-gray-500">{label}</p>
            </div>
            <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, vacante o etiqueta..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-amber-400 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={15} className="text-gray-400" />
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
          >
            <option value="">Todas las clasificaciones</option>
            {CLASSIFICATIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {allTags.length > 0 && (
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
            >
              <option value="">Todas las etiquetas</option>
              {allTags.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          )}
          {(search || filterClass || filterTag) && (
            <button
              onClick={() => { setSearch(''); setFilterClass(''); setFilterTag('') }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-500 hover:bg-gray-50"
            >
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Contenido */}
      {loading ? (
        <div className="py-16 text-center text-gray-400">Cargando banco de talento...</div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
          <Users className="mx-auto mb-3 text-gray-300" size={40} />
          <p className="font-semibold text-gray-700">El banco de talento está vacío</p>
          <p className="mt-1 text-sm text-gray-400">
            Agrega candidatos desde el módulo de Entrevistas usando el botón
            <span className="mx-1 font-medium text-gray-600">Enviar a banco de talento</span>.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-10 text-center">
          <p className="text-sm text-gray-400">No hay candidatos que coincidan con los filtros.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((entry) => (
            <TalentCard
              key={entry._id}
              entry={entry}
              onEdit={() => setEditingEntry(entry)}
              onDelete={() => handleDelete(entry)}
            />
          ))}
        </div>
      )}

      {/* Modal editar */}
      {editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSave={async (patch) => {
            try {
              await updateEntry(editingEntry._id, patch)
              setEditingEntry(null)
              if (user?.userId) fetchByCompany(user.userId)
            } catch (e) {
              console.error('Error al actualizar:', e)
              alert('No se pudo guardar los cambios.')
            }
          }}
        />
      )}
    </EmpresaLayout>
  )
}

/* ---------- Tarjeta de candidato ---------- */
function TalentCard({
  entry,
  onEdit,
  onDelete,
}: {
  entry: TalentBankEntry
  onEdit: () => void
  onDelete: () => void
}) {
  const initials = (entry.collaboratorName || 'C')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        {entry.collaboratorPhoto ? (
          <img
            src={entry.collaboratorPhoto}
            alt={entry.collaboratorName}
            className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-bold text-slate-600">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold text-gray-900">{entry.collaboratorName || 'Candidato'}</p>
          <p className="truncate text-xs text-gray-400">{entry.collaboratorEmail || ''}</p>
          {entry.lastVacancyTitle && (
            <p className="mt-0.5 truncate text-xs text-gray-500">
              Vacante: <span className="font-medium">{entry.lastVacancyTitle}</span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span
          className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
            CLASS_STYLES[entry.classification || ''] || 'bg-slate-100 text-slate-600'
          }`}
        >
          {entry.classification || 'Por clasificar'}
        </span>
        {typeof entry.lastScore === 'number' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700">
            <Star size={10} /> {entry.lastScore}
          </span>
        )}
      </div>

      {(entry.tags || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {(entry.tags || []).map((tag) => (
            <span key={tag} className="rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {entry.notes && (
        <p className="mt-2 line-clamp-2 text-xs italic text-gray-400">{entry.notes}</p>
      )}

      <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3">
        <button
          onClick={onEdit}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          <Edit2 size={12} /> Editar
        </button>
        <button
          onClick={onDelete}
          className="ml-auto inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
        >
          <Trash2 size={12} /> Eliminar
        </button>
      </div>
    </div>
  )
}

/* ---------- Modal editar entrada ---------- */
function EditEntryModal({
  entry,
  onClose,
  onSave,
}: {
  entry: TalentBankEntry
  onClose: () => void
  onSave: (patch: Partial<TalentBankEntry>) => void
}) {
  const [classification, setClassification] = useState(entry.classification || 'Por clasificar')
  const [notes, setNotes] = useState(entry.notes || '')
  const [tagsInput, setTagsInput] = useState((entry.tags || []).join(', '))

  const handleSave = () => {
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
    onSave({ classification, notes, tags })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-base font-bold text-gray-900">
            Editar — {entry.collaboratorName}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Clasificación</label>
            <select
              value={classification}
              onChange={(e) => setClassification(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
            >
              {CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Etiquetas</label>
            <input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="Ej. liderazgo, bilingüe, experiencia retail"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
            />
            <p className="mt-1 text-xs text-gray-400">Separa las etiquetas por coma.</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Notas internas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Observaciones sobre el candidato para futuras búsquedas..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="rounded-lg bg-amber-400 px-5 py-2 text-sm font-bold text-slate-900 hover:bg-amber-300"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  )
}

export default EmpresaBancoTalento
